import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Boxes, Calculator, ClipboardList, Wrench, Check, ChevronDown, Sparkles, FolderOpen, RotateCcw, Download } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import Breadcrumbs, { toLdItems } from "@/components/Breadcrumbs";
import { buildBreadcrumbLd, buildFaqLd } from "@/lib/seo";
import { useSeoT, PAGE_META } from "@/lib/seoPagesI18n";
import { useToolsT } from "@/lib/toolsI18n";
import { useProject, getVolumeTotal, getBoxTotal, getInventoryCounts, track } from "@/lib/projectStore";
import { generateProjectPdf } from "@/lib/projectPdf";

const H2 = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const TOOLS = [
  { slug: "calculateur-volume-demenagement", icon: Boxes },
  { slug: "calculateur-cartons", icon: Calculator },
  { slug: "inventaire-demenagement", icon: ClipboardList },
];

export default function OutilsIndex() {
  const navigate = useNavigate();
  const { t: st, lang } = useSeoT();
  const T = useToolsT(lang);
  const P = T.page;
  const meta = (PAGE_META[lang] || PAGE_META.fr).outils;
  const { project, update, reset } = useProject(false);
  const [openFaq, setOpenFaq] = React.useState(-1);
  React.useEffect(() => { track("tools_page_view"); }, []);

  const hasProject = !!project;
  const vol = getVolumeTotal(project);
  const boxes = getBoxTotal(project);
  const invc = getInventoryCounts(project);
  const bc = [{ name: st.bc.home, path: "/" }, { name: st.bc.outils, path: "/outils" }];

  const createProject = () => { update((p) => p); track("project_create"); navigate("/outils/calculateur-volume-demenagement"); };
  const jsonLd = [buildBreadcrumbLd(toLdItems(bc)), buildFaqLd(P.faq.map((f) => ({ q: f.q, a: f.a })))];

  return (
    <div className="min-h-screen bg-white text-[#0A0F2C]" data-testid="outils-index">
      <Seo title={meta.title} description={meta.desc} path="/outils" jsonLd={jsonLd} />
      <Header />

      {/* HERO */}
      <section className="relative bg-[#2D5BFF] hero-noise overflow-hidden pt-28 md:pt-36 pb-14 md:pb-20 px-5 sm:px-8 md:px-10 lg:px-14 text-white">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(255,255,255,0.15), transparent 55%)" }} />
        <div className="relative mx-auto" style={{ maxWidth: "1280px" }}>
          <div className="mb-6"><Breadcrumbs items={bc} variant="dark" /></div>
          <h1 className="font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-3xl" style={H2}>{P.h1a} <span className="text-[#FFD84D]">{P.h1b}</span></h1>
          <p className="mt-5 text-white/85 text-base md:text-lg max-w-2xl">{P.heroSub}</p>
          <div className="mt-6 text-white/55 text-sm">{P.reassure}</div>
        </div>
      </section>

      {/* MON PROJET */}
      <section className="py-10 md:py-14 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto" style={{ maxWidth: "1280px" }}>
          <div className="rounded-3xl border-2 border-[#EAF0FF] bg-[#F7F9FF] p-6 md:p-8" data-testid="outils-project">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF] mb-3"><FolderOpen size={14} /> {P.project.title}</div>
            {!hasProject ? (
              <div className="flex flex-col md:flex-row md:items-center gap-5 justify-between">
                <div><h2 className="font-extrabold text-2xl" style={H2}>{P.project.emptyTitle}</h2><p className="mt-2 text-[#4B5563] text-[15px] max-w-xl">{P.project.emptyText}</p></div>
                <button onClick={createProject} className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#2D5BFF] hover:bg-[#0A0F2C] text-white px-6 py-3.5 font-extrabold text-sm transition-all" data-testid="outils-project-create">{P.project.create} <ArrowRight size={15} /></button>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat label={P.project.volume} value={vol > 0 ? `${vol} m³` : "—"} />
                  <Stat label={P.project.boxes} value={boxes > 0 ? boxes : "—"} />
                  <Stat label={P.project.inventory} value={invc.items > 0 ? `${invc.items} ${P.project.elements}` : "—"} />
                  <Stat label={P.project.rooms} value={invc.rooms > 0 ? invc.rooms : "—"} />
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button onClick={() => { track("project_continue"); navigate("/outils/calculateur-volume-demenagement"); }} className="inline-flex items-center gap-2 rounded-full bg-[#2D5BFF] hover:bg-[#0A0F2C] text-white px-6 py-3.5 font-extrabold text-sm transition-all" data-testid="outils-project-continue">{P.project.continue} <ArrowRight size={15} /></button>
                  <button onClick={() => { track("project_pdf"); generateProjectPdf(project, T, lang); }} className="inline-flex items-center gap-2 rounded-full bg-[#0A0F2C] hover:bg-[#1a2050] text-white px-6 py-3.5 font-extrabold text-sm transition-all" data-testid="outils-project-pdf"><Download size={15} /> {T.pdf.download}</button>
                  <button onClick={() => { if (window.confirm(P.project.resetConfirm)) reset(); }} className="inline-flex items-center gap-2 rounded-full text-[#6B7280] hover:text-[#0A0F2C] px-4 py-3.5 font-semibold text-sm" data-testid="outils-project-reset"><RotateCcw size={14} /> {P.project.reset}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CARTES OUTILS */}
      <section id="tools" className="scroll-mt-24 pb-6 md:pb-10 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto" style={{ maxWidth: "1280px" }}>
          <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight mb-8" style={H2}>{P.toolsTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TOOLS.map((t) => {
              const Ic = t.icon; const cc = P.cards[t.slug];
              return (
                <Link key={t.slug} to={`/outils/${t.slug}`} className="group flex flex-col bg-[#F7F7F8] hover:bg-[#EAF0FF] rounded-3xl p-7 transition-colors" data-testid={`tool-card-${t.slug}`}>
                  <div className="flex items-center">
                    <div className="w-11 h-11 rounded-xl bg-white text-[#2D5BFF] inline-flex items-center justify-center shadow-sm"><Ic size={20} /></div>
                  </div>
                  <h3 className="mt-4 font-extrabold text-xl group-hover:text-[#2D5BFF] transition-colors" style={H2}>{cc.name}</h3>
                  <p className="mt-2 text-sm text-[#4B5563] leading-relaxed flex-1">{cc.desc}</p>
                  <p className="mt-3 text-[13px] text-[#2D5BFF] font-semibold flex items-start gap-1.5"><Sparkles size={13} className="mt-0.5 shrink-0" /> {cc.benefit}</p>
                  <span className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD84D] hover:bg-[#FFB800] text-[#0A0F2C] px-5 py-3 font-extrabold text-sm active:scale-[0.98] transition-all group-hover:shadow-[0_15px_30px_-8px_rgba(255,216,77,0.55)] w-full">{cc.cta} <ArrowRight size={14} /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* UN PROJET PLUSIEURS OUTILS */}
      <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#0A0F2C] text-white">
        <div className="mx-auto text-center" style={{ maxWidth: "900px" }}>
          <h2 className="font-extrabold text-3xl md:text-4xl tracking-tight" style={H2}>{P.flowTitle}</h2>
          <p className="mt-4 text-white/75 text-base md:text-lg">{P.flowText}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
            {P.flowSteps.map((s, i) => (
              <React.Fragment key={i}>
                <span className="rounded-full bg-white/10 border border-white/15 px-4 py-2 text-sm font-bold">{s}</span>
                {i < P.flowSteps.length - 1 && <ArrowRight size={16} className="text-[#FFD84D]" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* POURQUOI */}
      <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto" style={{ maxWidth: "1280px" }}>
          <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight" style={H2}>{P.whyTitle}</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {P.why.map((w, i) => (
              <div key={i} className="bg-[#F7F7F8] rounded-2xl p-6" data-testid={`outils-why-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-[#EAF0FF] text-[#2D5BFF] inline-flex items-center justify-center"><Check size={18} strokeWidth={3} /></div>
                <h3 className="mt-4 font-extrabold text-base" style={H2}>{w.t}</h3>
                <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMENT CA MARCHE */}
      <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#F7F7F8]">
        <div className="mx-auto" style={{ maxWidth: "1080px" }}>
          <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight" style={H2}>{P.howTitle}</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {P.how.map((h, i) => (
              <div key={i} className="bg-white rounded-3xl p-7" data-testid={`outils-how-${i}`}>
                <div className="text-[#2D5BFF] font-extrabold text-3xl tabular-nums">{i + 1}</div>
                <h3 className="mt-3 font-extrabold text-lg" style={H2}>{h.t}</h3>
                <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{h.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto" style={{ maxWidth: "820px" }}>
          <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight text-center" style={H2}>{P.faqTitle}</h2>
          <div className="mt-8 flex flex-col gap-3">
            {P.faq.map((it, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="bg-[#F7F7F8] rounded-2xl overflow-hidden" data-testid={`outils-faq-${i}`}>
                  <button className="w-full text-left p-5 flex items-center justify-between gap-4" onClick={() => setOpenFaq(open ? -1 : i)} aria-expanded={open}>
                    <span className="font-bold text-[15px]">{it.q}</span>
                    <ChevronDown className={`shrink-0 text-[#2D5BFF] transition-transform ${open ? "rotate-180" : ""}`} size={18} />
                  </button>
                  {open && <div className="px-5 pb-5 text-[#4B5563] text-sm leading-relaxed">{it.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#2D5BFF] text-white">
        <div className="mx-auto text-center" style={{ maxWidth: "760px" }}>
          <h2 className="font-extrabold text-3xl md:text-4xl tracking-tighter" style={H2}>{P.finalTitle}</h2>
          <p className="mt-4 text-white/85 text-base md:text-lg">{P.finalText}</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/devis" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD84D] hover:bg-yellow-300 text-[#0A0F2C] px-8 py-4 font-extrabold text-sm transition-all" data-testid="outils-final-quote">{P.finalCta} <ArrowRight size={16} /></Link>
            <Link to="/services" className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/25 text-white px-8 py-4 font-semibold text-sm transition-all" data-testid="outils-final-services">{P.finalCta2}</Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-white p-4 border border-gray-100">
      <div className="text-[11px] text-[#9CA3AF] font-bold uppercase tracking-wide">{label}</div>
      <div className="mt-1 font-extrabold text-xl tabular-nums" style={H2}>{value}</div>
    </div>
  );
}
